/* eslint-disable react/no-array-index-key */
import Text from '@deprecated-ui/common/Text';
import color from '@deprecated-ui/_style/color';
import Link from 'next/link';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import stringReplace from 'react-string-replace-recursively';
import { parseEmoji } from 'utils/stream';
import { selectors } from 'lib/entities/credentials';
import { useSelector } from 'react-redux';
import { InsiderEventData } from 'features/insider/types';
import { highlightWords } from '../utils';
import { PopoverCompany, PopoverUser } from '../Popover';
import { FormatedText, TextContainer } from './styled';

interface TextContentProps {
  content: string;
  maskedHtml: any;
  style?: any;
  isDefaultExpand?: boolean;
  isRepost?: boolean;
  highlightWord?: string;
  needParsed?: boolean;
  [key: string]: any;
}

function TextContent({
  content,
  maskedHtml,
  style = {},
  isDefaultExpand = false,
  isRepost = false,
  highlightWord = '',
  needParsed = true,
  ...props
}: TextContentProps) {
  const defaultHeight = 200;
  const MAX_TEXT_LENGTH = 400;
  const MAX_TRIM_TEXT = 140;

  const textRef = useRef<HTMLParagraphElement | null>(null);

  const [contentHeight, setContentHeight] = useState<number>(defaultHeight);
  const [isExpand, setIsExpand] = useState<boolean>(isDefaultExpand);
  const [showReadMore, setShowReadMore] = useState(false);

  const { isLoggedIn } = useSelector(selectors);

  // Due to different format between exodus API and base API regarding `mask_html` data format
  // we need to transform the `mask_html` data to object first to support both api
  const parseHtmlMask = (mask) => {
    if (Array.isArray(maskedHtml)) {
      const parsed = mask.reduce((obj, curr) => {
        // eslint-disable-next-line no-param-reassign
        obj[curr.mask] = curr;

        return obj;
      }, {});

      return parsed;
    }

    return mask;
  };

  const parsedText = useMemo(() => {
    let tempParsedText = parseEmoji(content);

    if (isRepost && tempParsedText?.length > MAX_TEXT_LENGTH) {
      // trim the string to the maximum length
      const trimmedString = tempParsedText.substr(0, MAX_TEXT_LENGTH);

      // re-trim if we are in the middle of a word
      tempParsedText = trimmedString.substr(
        0,
        Math.min(trimmedString.length, trimmedString.lastIndexOf(' ')),
      );
    }

    const parsedHtml = parseHtmlMask(maskedHtml);

    const config = {
      breaks: {
        pattern: /(\n|\r\n)/g,
        matcherFn: () => (
          <>
            <br />
          </>
        ),
      },
      symbols: {
        pattern: /(\[%[a-z0-9]*%\])/gi,
        textFn: (text) => {
          const cleanedSymbol = text.match(/\w+/g);
          return cleanedSymbol;
        },
        matcherFn: (rawText) => {
          const htmlData = parsedHtml[rawText];
          const { attr = {}, text, tag = '' } = htmlData;
          const textContent = text.substr(1);
          const textColor = htmlData?.attr?.color?.light;

          const attrClass = attr?.class || '';

          const isSymbolLink =
            attrClass === 'stock-indicator' ||
            attrClass === 'fr-bonds-indicator';
          if (isSymbolLink) {
            return isLoggedIn ? (
              <Link href={`/${attr.href}`}>
                <a target="_blank" rel="noopener noreferrer">
                  <PopoverCompany symbol={textContent}>{text}</PopoverCompany>
                </a>
              </Link>
            ) : (
              <Text as="span" color={color.primary}>
                {text}
              </Text>
            );
          }

          if (attrClass === 'user-indicator') {
            return isLoggedIn ? (
              <>
                {tag === 'deactivatedUser' ? (
                  <Link href={`/${attr.href}`}>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      className="deactive-user-label"
                    >
                      {text}
                    </a>
                  </Link>
                ) : (
                  <Link href={`/${attr.href}`}>
                    <a target="_blank" rel="noopener noreferrer">
                      <PopoverUser username={textContent}>{text}</PopoverUser>
                    </a>
                  </Link>
                )}
              </>
            ) : (
              <Text as="span" color={color.primary}>
                {text}
              </Text>
            );
          }
          if (tag === 'span' && attrClass === 'insider-indicator') {
            return (
              <Text
                as="span"
                style={textColor !== '' ? { color: textColor } : {}}
              >
                {text}
              </Text>
            );
          }
          if (tag === 'a' && attrClass === 'insider-indicator') {
            return isLoggedIn ? (
              <button
                style={{
                  color: '#0BA16B',
                  cursor: 'pointer',
                  border: 'none',
                  background: 'none',
                  display: 'inline',
                }}
                type="button"
                onClick={() => {
                  const str = attr.href ?? '';
                  const id = str.split('/')?.[1];
                  const isForeign = content.includes('Foreign');
                  const nationality = isForeign
                    ? 'NATIONALITY_TYPE_FOREIGN'
                    : 'NATIONALITY_TYPE_LOCAL';
                  const customEvent = new CustomEvent<InsiderEventData>(
                    'host-insider-activity',
                    {
                      detail: {
                        eventName: 'insider-detail',
                        id,
                        name: attr.data_insider,
                        nationality,
                      },
                    },
                  );
                  window.dispatchEvent(customEvent);
                }}
              >
                {text}
              </button>
            ) : (
              <Text as="span" color={color.primary}>
                {text}
              </Text>
            );
          }

          if (tag === 'a') {
            return (
              <Link href={attr.href}>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  className={attrClass}
                >
                  {text}
                </a>
              </Link>
            );
          }

          if (tag === 'b') {
            return <b className={attrClass}>{text}</b>;
          }

          if (tag === 'span') {
            return <span className={attrClass}>{text}</span>;
          }

          return <p className={attrClass}>{text}</p>;
        },
      },
    };

    if (needParsed) {
      tempParsedText = stringReplace(config)(tempParsedText);
    } else {
      tempParsedText =
        tempParsedText.length > MAX_TRIM_TEXT
          ? [`${tempParsedText.substring(0, MAX_TRIM_TEXT)} ...`]
          : [tempParsedText];
    }

    return tempParsedText;
  }, [content]);

  const handleExpand = () => {
    const text = textRef.current;
    setIsExpand(true);
    setContentHeight(text.offsetHeight);
  };

  useEffect(() => {
    if (!textRef?.current) return;

    const { offsetHeight } = textRef.current;

    const isHeightOverflow = offsetHeight > contentHeight;
    setShowReadMore(isHeightOverflow);

    if (isDefaultExpand && !isRepost) {
      setContentHeight(offsetHeight);
    }

    if (highlightWord !== '') {
      highlightWords(highlightWord, textRef.current, 'mark');
    }
  }, []);

  const hideLoggedInText = !isExpand && showReadMore && isLoggedIn;

  return (
    <>
      <TextContainer
        style={{ maxHeight: `${contentHeight}px`, ...style }}
        key="stream-formatted-text"
      >
        <FormatedText
          padding="0px"
          height="1.55"
          ref={textRef}
          style={{ wordWrap: !isRepost ? 'break-word' : 'unset' }}
          {...props}
        >
          {/* PARSED TEXT RENDER TO PREVENT UNIQUE KEY ERROR */}
          {parsedText?.map((item, i) => (
            <span key={i}>{item}</span>
          ))}
        </FormatedText>
      </TextContainer>
      {hideLoggedInText && !isRepost && (
        <Text
          color={color.primary}
          onClick={handleExpand}
          style={{ cursor: 'pointer' }}
          key="stream-readmore-text"
        >
          Read more...
        </Text>
      )}
      {!isExpand && content.length > MAX_TEXT_LENGTH && !isLoggedIn && (
        <Link href="/register">
          <Text color={color.primary} as="a">
            Read more...
          </Text>
        </Link>
      )}
    </>
  );
}

export default memo(TextContent);
